import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Eye, Trash2, RotateCcw, Skull, UserCheck, UserX,
  Ban, UserCog, Users, AlertTriangle, CheckCircle2, XCircle,
  ChevronLeft, ChevronRight, Loader2, Key, Copy, Check,
  SlidersHorizontal, Filter,
} from 'lucide-react';
import { adminAPI } from '../../../services/api';
import { toast } from 'react-toastify';
import ActionMenu from '../../shared/ActionMenu';

/* ── design tokens ── */
const C = {
  bg: '#f4f4f9', surface: '#FFFFFF', border: '#e8e8f1', borderSoft: '#f0f0f6',
  ink: '#14142b', body: '#4a4a6a', muted: '#6e6e86',
  primary: '#7c3aed', primarySoft: '#f1ebff', primaryTint: '#ddd6fe',
  success: '#10B981', successSoft: '#D1FAE5',
  warning: '#F59E0B', warnSoft: '#FEF3C7',
  danger: '#EF4444', dangerSoft: '#FEE2E2',
  radius: 14, radiusSm: 8,
};

const fmtDate = (iso) => iso
  ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

const fmtINR = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* ── StatusBadge ── */
const StatusBadge = ({ status }) => {
  const map = {
    approved:  { bg: C.successSoft, color: C.success, label: 'Approved' },
    pending:   { bg: C.warnSoft,    color: C.warning, label: 'Pending'  },
    rejected:  { bg: C.dangerSoft,  color: C.danger,  label: 'Rejected' },
    suspended: { bg: '#F1F5F9',     color: C.muted,   label: 'Suspended'},
  };
  const s = map[status] || { bg: '#F1F5F9', color: C.muted, label: status };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 999,
      background: s.bg, color: s.color, fontSize: 11, fontWeight: 700,
      textTransform: 'capitalize', letterSpacing: .3,
    }}>{s.label}</span>
  );
};

/* ── small icon action button ── */
const Btn = ({ icon: Icon, color, bg, onClick, title, loading }) => (
  <button
    onClick={onClick}
    title={title}
    disabled={loading}
    style={{
      width: 30, height: 30, borderRadius: 7, border: 'none',
      background: bg, color, cursor: loading ? 'not-allowed' : 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      opacity: loading ? .5 : 1, flexShrink: 0,
    }}
  >
    {loading ? <Loader2 size={13} style={{ animation: 'spin .8s linear infinite' }} /> : <Icon size={13} strokeWidth={2} />}
  </button>
);

/* ── confirmation modal ── */
const ConfirmModal = ({ cfg, user, onConfirm, onCancel }) => (
  <div onClick={onCancel} style={{
    position: 'fixed', inset: 0, background: 'rgba(20,20,43,.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, backdropFilter: 'blur(3px)',
  }}>
    <div onClick={e => e.stopPropagation()} style={{
      background: C.surface, borderRadius: C.radius + 2,
      width: '100%', maxWidth: 400, padding: '28px 28px 22px',
      boxShadow: '0 20px 60px rgba(20,20,43,.18)',
    }}>
      <div style={{
        width: 48, height: 48, borderRadius: '50%',
        background: cfg.iconBg, color: cfg.iconColor,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 16px',
      }}>
        <cfg.Icon size={20} strokeWidth={1.8} />
      </div>
      <h3 style={{ fontSize: 17, fontWeight: 800, color: C.ink, margin: '0 0 8px', textAlign: 'center' }}>{cfg.title}</h3>
      <p style={{ fontSize: 13, color: C.body, textAlign: 'center', lineHeight: 1.6, margin: '0 0 24px' }}>
        {cfg.body(user)}
      </p>
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={onCancel} style={{
          flex: 1, padding: '10px 0', borderRadius: C.radiusSm,
          background: C.borderSoft, color: C.body, border: `1px solid ${C.border}`,
          fontWeight: 600, fontSize: 13, cursor: 'pointer',
        }}>Cancel</button>
        <button onClick={onConfirm} style={{
          flex: 1, padding: '10px 0', borderRadius: C.radiusSm,
          background: cfg.btnColor, color: '#fff', border: 'none',
          fontWeight: 700, fontSize: 13, cursor: 'pointer',
        }}>{cfg.btn}</button>
      </div>
    </div>
  </div>
);

/* ── credentials modal ── */
const CredsModal = ({ data, onClose }) => {
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedSalt, setCopiedSalt] = useState(false);

  const copy = (val, setter) => {
    navigator.clipboard.writeText(val);
    setter(true);
    setTimeout(() => setter(false), 1500);
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(20,20,43,.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, backdropFilter: 'blur(3px)',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.surface, borderRadius: C.radius + 2,
        width: '100%', maxWidth: 480, padding: '28px',
        boxShadow: '0 20px 60px rgba(20,20,43,.18)',
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%',
          background: C.primarySoft, color: C.primary,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 16px',
        }}>
          <Key size={20} strokeWidth={1.8} />
        </div>
        <h3 style={{ fontSize: 17, fontWeight: 800, color: C.ink, margin: '0 0 6px', textAlign: 'center' }}>
          Credentials Issued
        </h3>
        <p style={{ fontSize: 13, color: C.muted, textAlign: 'center', margin: '0 0 20px', lineHeight: 1.5 }}>
          Share with <strong style={{ color: C.ink }}>{data.user?.username}</strong>.
          The <strong>secret_salt</strong> is shown <strong>only once</strong> — copy it now.
        </p>

        {[
          { label: 'API Key', val: data.api_key, copied: copiedKey, setCopied: setCopiedKey },
          { label: 'Secret Salt (one-time)', val: data.secret_salt, copied: copiedSalt, setCopied: setCopiedSalt },
        ].map(({ label, val, copied, setCopied }) => (
          <div key={label} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: .4, textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: '#14142b', borderRadius: C.radiusSm, padding: '10px 14px',
            }}>
              <code style={{ flex: 1, fontSize: 12, color: '#e8e8f1', wordBreak: 'break-all', fontFamily: 'Menlo,Consolas,monospace' }}>{val}</code>
              <button onClick={() => copy(val, setCopied)} style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: copied ? C.success : C.muted, flexShrink: 0,
              }}>
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
          </div>
        ))}

        <button onClick={onClose} style={{
          width: '100%', padding: '10px 0', marginTop: 8,
          borderRadius: C.radiusSm, background: C.primary, color: '#fff',
          border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer',
        }}>I've saved it</button>
      </div>
    </div>
  );
};

/* ── modal config ── */
const MODAL_CFG = {
  approve: {
    Icon: UserCheck, iconBg: C.successSoft, iconColor: C.success,
    title: 'Approve User', btn: 'Approve & Activate', btnColor: C.success,
    body: (u) => `Approve "${u?.username}"? This activates their account and issues API credentials.`,
  },
  reject: {
    Icon: UserX, iconBg: C.dangerSoft, iconColor: C.danger,
    title: 'Reject User', btn: 'Reject', btnColor: C.danger,
    body: (u) => `Reject "${u?.username}"? Their account will be marked rejected.`,
  },
  suspend: {
    Icon: Ban, iconBg: C.warnSoft, iconColor: C.warning,
    title: 'Suspend User', btn: 'Suspend', btnColor: C.warning,
    body: (u) => `Suspend "${u?.username}"? Their account will be locked until you reactivate.`,
  },
  login: {
    Icon: UserCog, iconBg: C.primarySoft, iconColor: C.primary,
    title: 'Login as User', btn: 'Impersonate', btnColor: C.primary,
    body: (u) => `You will be impersonating "${u?.username}" without their password. Your admin session will be saved.`,
  },
  soft: {
    Icon: Trash2, iconBg: C.warnSoft, iconColor: C.warning,
    title: 'Soft Delete User', btn: 'Soft Delete', btnColor: C.warning,
    body: (u) => `Soft delete "${u?.username}"? Data is preserved and can be restored anytime.`,
  },
  restore: {
    Icon: RotateCcw, iconBg: C.successSoft, iconColor: C.success,
    title: 'Restore User', btn: 'Restore', btnColor: C.success,
    body: (u) => `Restore "${u?.username}" and make them active again?`,
  },
  hard: {
    Icon: Skull, iconBg: C.dangerSoft, iconColor: C.danger,
    title: 'Permanently Delete', btn: 'Delete Forever', btnColor: C.danger,
    body: (u) => `PERMANENTLY delete "${u?.username}" and all their data? This cannot be undone.`,
  },
};

/* ══════════════════════════════════════════════════ */
const AdminUsers = () => {
  const navigate = useNavigate();
  const [tab,          setTab]          = useState('active');
  const [loading,      setLoading]      = useState(true);
  const [users,        setUsers]        = useState([]);
  const [pagination,   setPagination]   = useState(null);
  const [actionLoading,setActionLoading]= useState(null);
  const [modal,        setModal]        = useState({ show: false, type: null, user: null });
  const [credsModal,   setCredsModal]   = useState({ show: false });
  const [filters,      setFilters]      = useState({ search: '', status: 'all', page: 1, per_page: 15 });
  const [stats,        setStats]        = useState({ total: 0, approved: 0, pending: 0, rejected: 0, suspended: 0 });

  useEffect(() => { tab === 'active' ? fetchUsers() : fetchTrashed(); }, [filters, tab]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const r = await adminAPI.users.getAll(filters);
      const list = r.data.data.users;
      setUsers(list);
      setPagination(r.data.data.pagination);
      setStats({
        total:     r.data.data.pagination?.total ?? list.length,
        approved:  list.filter(u => u.status === 'approved').length,
        pending:   list.filter(u => u.status === 'pending').length,
        rejected:  list.filter(u => u.status === 'rejected').length,
        suspended: list.filter(u => u.status === 'suspended').length,
      });
    } catch { toast.error('Failed to fetch users'); } finally { setLoading(false); }
  };
  const fetchTrashed = async () => {
    try { setLoading(true); const r = await adminAPI.users.getTrashed(); setUsers(r.data.data.users); setPagination(r.data.data.pagination); }
    catch { toast.error('Failed to fetch trashed users'); } finally { setLoading(false); }
  };
  const refresh = () => tab === 'active' ? fetchUsers() : fetchTrashed();
  const setFilter = (k, v) => setFilters(p => ({ ...p, [k]: v, page: 1 }));
  const setPage   = (p)    => setFilters(f => ({ ...f, page: p }));
  const openModal  = (type, user) => setModal({ show: true, type, user });
  const closeModal = ()           => setModal({ show: false, type: null, user: null });

  const handleConfirm = async () => {
    const { type, user } = modal; closeModal();
    setActionLoading(`${type}-${user.id}`);
    try {
      switch (type) {
        case 'soft':    await adminAPI.users.softDelete(user.id); toast.success(`"${user.username}" soft deleted.`); refresh(); break;
        case 'restore': await adminAPI.users.restore(user.id);    toast.success(`"${user.username}" restored.`);    refresh(); break;
        case 'hard':    await adminAPI.users.hardDelete(user.id); toast.success(`"${user.username}" permanently deleted.`); refresh(); break;
        case 'login': {
          const r = await adminAPI.users.loginAs(user.id);
          const { token, user: imp } = r.data.data;
          localStorage.setItem('impersonate_admin_token', localStorage.getItem('token'));
          localStorage.setItem('token', token);
          localStorage.setItem('user', JSON.stringify(imp));
          toast.success(`Now logged in as "${user.username}"`);
          navigate('/user/profile'); break;
        }
        case 'approve': {
          const r = await adminAPI.users.approve(user.id);
          toast.success(`"${user.username}" approved.`);
          const d = r.data?.data || {};
          if (d.secret_salt) setCredsModal({ show: true, user, api_key: d.api_key, secret_salt: d.secret_salt });
          refresh(); break;
        }
        case 'reject':  await adminAPI.users.reject(user.id);  toast.info(`"${user.username}" rejected.`);  refresh(); break;
        case 'suspend': await adminAPI.users.suspend(user.id); toast.info(`"${user.username}" suspended.`); refresh(); break;
        default: break;
      }
    } catch (e) { toast.error(e?.response?.data?.message || 'Action failed'); }
    finally { setActionLoading(null); }
  };

  /* pagination pages */
  const pages = (() => {
    if (!pagination) return [];
    const { last_page: t, current_page: c } = pagination;
    let s = Math.max(1, c - 2), e = Math.min(t, s + 4);
    if (e - s < 4) s = Math.max(1, e - 4);
    return Array.from({ length: e - s + 1 }, (_, i) => s + i);
  })();

  const inputStyle = {
    height: 38, borderRadius: C.radiusSm, border: `1px solid ${C.border}`,
    padding: '0 12px', fontSize: 13, color: C.ink, background: C.surface,
    outline: 'none', width: '100%',
  };
  const selectStyle = { ...inputStyle, cursor: 'pointer' };

  const qTabs = [
    { label: 'All',       val: 'all',       count: stats.total,     color: C.primary },
    { label: 'Approved',  val: 'approved',  count: stats.approved,  color: C.success },
    { label: 'Pending',   val: 'pending',   count: stats.pending,   color: C.warning },
    { label: 'Rejected',  val: 'rejected',  count: stats.rejected,  color: C.danger  },
    { label: 'Suspended', val: 'suspended', count: stats.suspended, color: C.muted   },
  ];

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        .usr-kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 20px; }
        @media (max-width: 1100px) { .usr-kpi-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 640px)  { .usr-kpi-grid { grid-template-columns: 1fr 1fr; } }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 22 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: C.ink, margin: 0, letterSpacing: '-.02em' }}>User Management</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: C.muted }}>Approve sign-ups, manage accounts, and impersonate users for support.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { key: 'active',  label: 'Active Users', Icon: Users,  activeColor: C.success },
            { key: 'trashed', label: 'Trash Bin',    Icon: Trash2, activeColor: C.danger  },
          ].map(({ key, label, Icon, activeColor }) => (
            <button key={key} onClick={() => { setTab(key); setFilters(f => ({ ...f, page: 1 })); }} style={{
              height: 38, padding: '0 14px', borderRadius: C.radiusSm,
              border: `1.5px solid ${tab === key ? activeColor : C.border}`,
              background: tab === key ? activeColor : C.surface,
              color: tab === key ? '#fff' : C.body,
              fontWeight: 700, fontSize: 13, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: 'inherit',
            }}>
              <Icon size={13} strokeWidth={2} />{label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="usr-kpi-grid">
        {[
          { label: 'Total Users',   value: stats.total,     color: C.primary, bg: C.primarySoft, Icon: Users,        val: 'all'       },
          { label: 'Approved',      value: stats.approved,  color: C.success, bg: C.successSoft, Icon: CheckCircle2, val: 'approved'  },
          { label: 'Pending',       value: stats.pending,   color: C.warning, bg: C.warnSoft,    Icon: AlertTriangle,val: 'pending'   },
          { label: 'Rejected',      value: stats.rejected,  color: C.danger,  bg: C.dangerSoft,  Icon: XCircle,      val: 'rejected'  },
        ].map(({ label, value, color, bg, Icon, val }) => {
          const active = filters.status === val;
          return (
            <div key={val} onClick={() => tab === 'active' && setFilter('status', val)} style={{
              background: C.surface, borderRadius: C.radius,
              border: `1.5px solid ${active ? color : C.border}`,
              padding: '18px 20px', cursor: tab === 'active' ? 'pointer' : 'default',
              boxShadow: active ? `0 4px 20px ${color}20` : '0 1px 4px rgba(20,20,43,0.04)',
              transition: 'all .18s', display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 6 }}>{label}</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: active ? color : C.ink, lineHeight: 1, letterSpacing: '-.02em' }}>{value}</div>
                </div>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: active ? color : bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={18} color={active ? '#fff' : color} strokeWidth={2} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filter Bar */}
      <div style={{ background: C.surface, borderRadius: C.radius, border: `1.5px solid ${C.border}`, marginBottom: 16, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          <div style={{ position: 'relative', flex: 1, borderRight: `1px solid ${C.borderSoft}` }}>
            <Search size={14} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: C.muted, pointerEvents: 'none' }} />
            <input
              style={{ ...inputStyle, paddingLeft: 38, border: 'none', height: 46, borderRadius: 0, background: 'transparent' }}
              placeholder="Search username, email, phone, MID…"
              value={filters.search}
              disabled={tab === 'trashed'}
              onChange={e => setFilter('search', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && refresh()}
            />
          </div>
          {tab === 'active' && (
            <div style={{ display: 'flex', padding: '0 12px', gap: 4, overflowX: 'auto', borderRight: `1px solid ${C.borderSoft}` }}>
              {qTabs.map(({ label, val, count, color }) => (
                <button key={val} onClick={() => setFilter('status', val)}
                  style={{ height: 30, padding: '0 12px', borderRadius: 999, border: `1.5px solid ${filters.status === val ? color : 'transparent'}`, background: filters.status === val ? color : 'transparent', color: filters.status === val ? '#fff' : C.muted, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5, transition: 'all .15s' }}>
                  {label}
                  <span style={{ background: filters.status === val ? 'rgba(255,255,255,0.25)' : C.borderSoft, color: filters.status === val ? '#fff' : C.muted, fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999 }}>{count}</span>
                </button>
              ))}
            </div>
          )}
          <div style={{ padding: '0 12px' }}>
            <button onClick={refresh} style={{ height: 36, padding: '0 18px', borderRadius: C.radiusSm, background: C.primary, color: '#fff', border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 7 }}>
              <Filter size={13} /> Apply
            </button>
          </div>
        </div>
      </div>

      {/* Table card */}
      <div style={{ background: C.surface, borderRadius: C.radius, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
        {tab === 'trashed' && (
          <div style={{ padding: '10px 20px', background: C.dangerSoft, borderBottom: `1px solid ${C.border}` }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: C.danger, display: 'flex', alignItems: 'center', gap: 7 }}>
              <Trash2 size={13} /> Showing soft-deleted users — you can restore or permanently delete them.
            </span>
          </div>
        )}

        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: C.muted }}>
            <Loader2 size={28} style={{ animation: 'spin .8s linear infinite' }} />
            <p style={{ marginTop: 12, fontSize: 13 }}>Loading…</p>
          </div>
        ) : users.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: C.muted }}>
            <Users size={36} style={{ opacity: .3, marginBottom: 12 }} />
            <p style={{ fontSize: 13 }}>{tab === 'trashed' ? 'Trash bin is empty.' : 'No users found.'}</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                    {['User', 'Contact', 'MID', 'Balance', 'Status', tab === 'trashed' ? 'Deleted At' : 'Joined', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: .5, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, i) => (
                    <tr key={u.id} style={{ borderBottom: `1px solid ${C.borderSoft}`, opacity: tab === 'trashed' ? .8 : 1, background: i % 2 === 0 ? C.surface : C.bg }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/users/${u.id}`)}>
                          <div style={{ fontWeight: 700, color: C.primary, fontSize: 13 }}>{u.username}</div>
                          <div style={{ fontSize: 11, color: C.muted }}>#{u.id}</div>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ color: C.ink, fontSize: 13 }}>{u.email}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>{u.phone}</div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <code style={{ fontSize: 11, color: C.muted, fontFamily: 'Menlo,Consolas,monospace' }}>{u.mid || '—'}</code>
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: C.ink }}>{fmtINR(u.wallet_balance)}</td>
                      <td style={{ padding: '12px 16px' }}><StatusBadge status={u.status} /></td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: C.muted }}>{fmtDate(tab === 'trashed' ? u.deleted_at : u.created_at)}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <ActionMenu items={tab === 'active' ? [
                          { label: 'View Details', Icon: Eye, color: C.primary, bg: C.primarySoft, onClick: () => navigate(`/admin/users/${u.id}`) },
                          { divider: true },
                          { label: 'Approve', Icon: UserCheck, color: C.success, bg: C.successSoft, loading: actionLoading === `approve-${u.id}`, onClick: () => openModal('approve', u), hidden: !(u.status === 'pending' || (u.account_status !== 'active' && u.status !== 'rejected')) },
                          { label: 'Reject', Icon: UserX, color: C.danger, bg: C.dangerSoft, loading: actionLoading === `reject-${u.id}`, onClick: () => openModal('reject', u), hidden: u.status !== 'pending' },
                          { label: 'Suspend', Icon: Ban, color: C.warning, bg: C.warnSoft, loading: actionLoading === `suspend-${u.id}`, onClick: () => openModal('suspend', u), hidden: !(u.status === 'approved' && u.account_status !== 'suspended') },
                          { label: 'Impersonate', Icon: UserCog, color: C.primary, bg: C.primarySoft, loading: actionLoading === `login-${u.id}`, onClick: () => openModal('login', u) },
                          { divider: true },
                          { label: 'Soft Delete', Icon: Trash2, color: C.warning, bg: C.warnSoft, loading: actionLoading === `soft-${u.id}`, onClick: () => openModal('soft', u) },
                          { label: 'Permanently Delete', Icon: Skull, color: C.danger, bg: C.dangerSoft, loading: actionLoading === `hard-${u.id}`, onClick: () => openModal('hard', u) },
                        ] : [
                          { label: 'Restore User', Icon: RotateCcw, color: C.success, bg: C.successSoft, loading: actionLoading === `restore-${u.id}`, onClick: () => openModal('restore', u) },
                          { label: 'Permanently Delete', Icon: Skull, color: C.danger, bg: C.dangerSoft, loading: actionLoading === `hard-${u.id}`, onClick: () => openModal('hard', u) },
                        ]} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.last_page > 1 && (
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '14px 20px', borderTop: `1px solid ${C.borderSoft}`,
              }}>
                <span style={{ fontSize: 12, color: C.muted }}>
                  Showing {pagination.from}–{pagination.to} of {pagination.total} users
                </span>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => setPage(pagination.current_page - 1)} disabled={pagination.current_page === 1}
                    style={{ width: 30, height: 30, borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, cursor: 'pointer', color: C.body, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ChevronLeft size={14} />
                  </button>
                  {pages.map(p => (
                    <button key={p} onClick={() => setPage(p)} style={{
                      width: 30, height: 30, borderRadius: 6,
                      border: `1px solid ${p === pagination.current_page ? C.primary : C.border}`,
                      background: p === pagination.current_page ? C.primary : C.surface,
                      color: p === pagination.current_page ? '#fff' : C.body,
                      fontWeight: p === pagination.current_page ? 700 : 400,
                      fontSize: 12, cursor: 'pointer',
                    }}>{p}</button>
                  ))}
                  <button onClick={() => setPage(pagination.current_page + 1)} disabled={pagination.current_page === pagination.last_page}
                    style={{ width: 30, height: 30, borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, cursor: 'pointer', color: C.body, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Confirm Modal */}
      {modal.show && MODAL_CFG[modal.type] && (
        <ConfirmModal
          cfg={MODAL_CFG[modal.type]}
          user={modal.user}
          onConfirm={handleConfirm}
          onCancel={closeModal}
        />
      )}

      {/* Credentials Modal */}
      {credsModal.show && (
        <CredsModal data={credsModal} onClose={() => setCredsModal({ show: false })} />
      )}
    </div>
  );
};

export default AdminUsers;
